// `useZaloShareHandler` — drives the Zalo share action shared by the
// desktop action-grid tile and the mobile More sheet row. Returns
// `{ busy, trigger }`; callers fire `trigger()` to open the native
// share sheet (or copy to clipboard with a "Mở Zalo" toast on
// fallback). Keeps the two surfaces in lock-step.

import { useCallback, useState } from "react";
import { toast } from "sonner";

import { useAuthStore } from "@/lib/auth-store";
import { shareOrCopy } from "@/lib/share";
import { formatPropertyShareText } from "@/features/property/utils/format-property-share-text";
import type { Property } from "@/types/property";

export function useZaloShareHandler(property: Property) {
  const user = useAuthStore((s) => s.user);
  const [busy, setBusy] = useState(false);

  const trigger = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      const text = formatPropertyShareText(property, user?.fullName);
      const outcome = await shareOrCopy({
        title: property.buildingName ?? "Bất động sản",
        text,
      });
      switch (outcome.kind) {
        case "shared":
          toast.success("Đã mở hộp thoại chia sẻ");
          break;
        case "copied":
          toast.success("Đã sao chép. Mở Zalo và dán vào hội thoại.", {
            action: {
              label: "Mở Zalo",
              onClick: () => {
                if (typeof window !== "undefined") {
                  window.open(
                    "https://chat.zalo.me/",
                    "_blank",
                    "noopener,noreferrer",
                  );
                }
              },
            },
          });
          break;
        case "cancelled":
          break;
        case "error":
          toast.error("Không thể chia sẻ. Vui lòng thử lại.");
          break;
      }
    } finally {
      setBusy(false);
    }
  }, [busy, property, user?.fullName]);

  return { busy, trigger };
}
